// EON feedback widget — boot bundle. MUST stay tiny and inert for non-reviewers (this runs on every
// pageview of customer sites). Heavy capture/UI code is split into chunks loaded on demand.
//
// Activation:
//   ?eon_reviewer_id=…  invited reviewer (URL token; persisted as a first-party cookie + localStorage)
//   ?eon_admin=…        team "Ver en el sitio" overlay (ephemeral, not persisted)
//   ?eon_feedback=<tok> group self-register link (secret, revocable) → show the widget, identify by email
//   a stored global person token (verified email) → identified everywhere
//   review with "feedback abierto" on → active for everyone (discovered via one init per session)

const TOKEN_PARAM = 'eon_reviewer_id';
const ADMIN_PARAM = 'eon_admin';
const FEEDBACK_PARAM = 'eon_feedback';
const PERSON_KEY = 'eon_person_token';
const REVIEWER_KEY = 'eon_reviewer_token'; // localStorage fallback
const RID_COOKIE = 'eon_rid'; // first-party cookie on the customer's own origin (survives navigation, visible)
const RID_MAX_AGE = 365 * 24 * 3600;
const JOIN_KEY = 'eon_join_token'; // sessionStorage: keep the group-link token across in-session navigation
const INERT_TTL_MS = 10_000; // bound stale closed verdicts when a review is opened while this tab is alive

function readReviewId(): string | null {
  const tag = document.querySelector('script[data-eon-review]') as HTMLScriptElement | null;
  return tag?.dataset.eonReview ?? null;
}
function ls(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}
function setCookie(name: string, value: string, maxAgeSec: number): void {
  try {
    document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSec}; Path=/; SameSite=Lax`;
  } catch {
    /* ignore */
  }
}

/** Resolve the reviewer/admin URL token, persisting an invited reviewer id so it survives navigation
 * (first-party cookie on the customer origin + localStorage fallback). The admin overlay is never
 * persisted (it's ephemeral by design). */
function urlToken(): { token: string; admin: boolean } | null {
  const url = new URL(location.href);
  const admin = url.searchParams.get(ADMIN_PARAM);
  if (admin) return { token: admin, admin: true };
  const rid = url.searchParams.get(TOKEN_PARAM);
  if (rid) {
    setCookie(RID_COOKIE, rid, RID_MAX_AGE);
    try {
      localStorage.setItem(REVIEWER_KEY, rid);
    } catch {
      /* private mode */
    }
    return { token: rid, admin: false };
  }
  // Later navigations drop the query param → fall back to the persisted token (cookie first).
  const stored = getCookie(RID_COOKIE) ?? ls(REVIEWER_KEY);
  if (stored) return { token: stored, admin: false };
  return null;
}

async function boot(): Promise<void> {
  const reviewId = readReviewId();
  if (!reviewId) return;

  const url = new URL(location.href);
  // Group self-register link ?eon_feedback=<token>. Persist for the session so it survives navigation
  // (the param drops off later pages) until the visitor verifies their email and gets a person token.
  let joinToken = url.searchParams.get(FEEDBACK_PARAM); // '' for bare ?eon_feedback, null if absent
  if (joinToken !== null) {
    try {
      sessionStorage.setItem(JOIN_KEY, joinToken);
    } catch {
      /* ignore */
    }
  } else {
    try {
      joinToken = sessionStorage.getItem(JOIN_KEY);
    } catch {
      /* ignore */
    }
  }
  const trigger = joinToken !== null; // the link is present now or earlier this session
  const tok = urlToken();
  const personToken = ls(PERSON_KEY) ?? '';
  const hasReason = !!tok || !!personToken || trigger;

  // Anonymous + no trigger: briefly cache a clean "closed" verdict. This is intentionally bounded:
  // Share can make feedback public while the tab is alive, so a session-long cache became stale.
  const inertKey = 'eon_inert_' + reviewId;
  if (!hasReason) {
    try {
      const cachedAt = Number(sessionStorage.getItem(inertKey));
      if (Number.isFinite(cachedAt) && Date.now() - cachedAt < INERT_TTL_MS) return;
      sessionStorage.removeItem(inertKey); // also clears the legacy value "1"
    } catch {
      /* ignore */
    }
  }

  const apiBase = new URL('.', import.meta.url).origin;
  const { start } = await import('./app');
  let active = false;
  try {
    active = await start({
      reviewId,
      token: tok?.token ?? '',
      admin: tok?.admin ?? false,
      personToken,
      joinToken: joinToken ?? '',
      apiBase,
    });
  } catch {
    // Origin not yet allowed / network error: do NOT cache inert — a later navigation may autoconnect
    // the domain or recover. Caching here is what made first-load autoconnect look broken.
    return;
  }
  // Clean "allowed origin, review closed, no identity" verdict → suppress only immediate retries.
  if (!active && !hasReason) {
    try {
      sessionStorage.setItem(inertKey, String(Date.now()));
    } catch {
      /* ignore */
    }
  }
}

void boot();
